<template>
  <v-dialog :value="value" max-width="820" @input="$emit('input', $event)">
    <v-card class="pa-6 fb-card">
      <h3 class="mb-4 black--text">{{ t("doYouAgree") }}</h3>

      <!-- Agree / Disagree -->
      <div class="mb-5 d-flex">
        <div class="mr-6 text-center">
          <v-btn
            fab
            depressed
            :color="agree === true ? 'green' : 'white'"
            :outlined="agree !== true"
            :dark="agree === true"
            class="fb-choice"
            @click="agree = true"
          >
            <v-icon :color="agree === true ? 'white' : 'grey'">mdi-thumb-up</v-icon>
          </v-btn>
          <div class="mt-1 black--text">{{ t("agree") }}</div>
        </div>
        <div class="text-center">
          <v-btn
            fab
            depressed
            :color="agree === false ? 'red' : 'white'"
            :outlined="agree !== false"
            :dark="agree === false"
            class="fb-choice"
            @click="agree = false"
          >
            <v-icon :color="agree === false ? 'white' : 'grey'">mdi-thumb-down</v-icon>
          </v-btn>
          <div class="mt-1 black--text">{{ t("disagree") }}</div>
        </div>
      </div>

      <!-- Disagree message -->
      <div v-if="agree === false" class="mb-5">
        <p class="mb-2 black--text">{{ t("disagreeHelp") }}</p>
        <div class="mb-1 grey--text text-body-2">{{ t("addFeedback") }}</div>
        <v-textarea
          v-model="message"
          :placeholder="t('feedbackPlaceholder')"
          outlined
          rows="5"
          hide-details
        />
      </div>

      <!-- Attachments -->
      <h4 class="mb-1 black--text">{{ t("attachmentsOptional") }}</h4>
      <p class="mb-2 grey--text text-body-2">{{ t("attachmentsHelp") }}</p>

      <div
        class="fb-dropzone"
        :class="{ 'fb-dropzone--over': dragOver }"
        @click="triggerBrowse"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
      >
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,video/mp4"
          multiple
          class="d-none"
          @change="onBrowse"
        />
        <v-icon size="34" color="#2f6fed">mdi-tray-arrow-up</v-icon>
        <div class="mt-2 fb-link">{{ t("selectFilesToUpload") }}</div>
        <div class="mt-1 grey--text">{{ t("orDragAndDrop") }}</div>
      </div>

      <!-- Selected files -->
      <div v-if="files.length" class="flex-wrap mt-3 d-flex">
        <v-chip
          v-for="(f, i) in files"
          :key="i"
          class="mb-2 mr-2"
          close
          @click:close="removeFile(i)"
        >
          <v-icon left small>{{ f.type.startsWith("video/") ? "mdi-video" : "mdi-image" }}</v-icon>
          {{ f.name }}
        </v-chip>
      </div>

      <div class="mt-5">
        <v-btn
          color="green"
          dark
          depressed
          large
          :loading="submitting"
          :disabled="agree === null"
          @click="submit"
        >
          {{ t("submit") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import S3UploadService from "@/_services/S3UploadService";

const ACCEPTED = ["image/jpeg", "image/png", "video/mp4"];

/**
 * Reusable AI-report feedback entry dialog (agree/disagree + message +
 * attachments). Decoupled from any single report module via props:
 *   - `service`      — object exposing `submitFeedback(reportUid, payload)`
 *   - `i18nPrefix`   — i18n namespace holding the feedback labels
 *   - `uploadFolder` — S3 folder for attachment uploads
 *
 * Emits `submitted` on success. The backend returns handled errors as HTTP 200
 * with `success:false`, so both that and thrown errors are surfaced.
 */
export default {
  name: "AiReportFeedbackDialog",
  props: {
    value: { type: Boolean, default: false },
    reportUid: { type: String, required: true },
    service: { type: Object, required: true },
    i18nPrefix: { type: String, required: true },
    uploadFolder: { type: String, default: "ai-reports/feedback" },
    // Preselected agree/disagree the dialog opens with (null = nothing selected).
    defaultAgree: { type: Boolean, default: null },
  },
  data() {
    return {
      agree: null,
      message: "",
      files: [],
      submitting: false,
      dragOver: false,
    };
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    t(key) {
      return this.$t(`${this.i18nPrefix}.${key}`);
    },
    reset() {
      // Open preset to the choice made in the display prompt (if any).
      this.agree = this.defaultAgree;
      this.message = "";
      this.files = [];
      this.submitting = false;
      this.dragOver = false;
    },
    triggerBrowse() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onBrowse(e) {
      this.addFiles(e.target.files);
      e.target.value = ""; // allow re-selecting the same file
    },
    onDrop(e) {
      this.dragOver = false;
      this.addFiles(e.dataTransfer.files);
    },
    addFiles(fileList) {
      const incoming = Array.from(fileList || []).filter((f) => ACCEPTED.includes(f.type));
      if (!incoming.length) return;
      this.files = [...this.files, ...incoming];
    },
    removeFile(i) {
      this.files.splice(i, 1);
    },
    fileType(mime) {
      if (!mime) return null;
      if (mime.startsWith("image/")) return "image";
      if (mime.startsWith("video/")) return "video";
      return null;
    },
    async submit() {
      if (this.agree === null) return;
      this.submitting = true;
      try {
        const attachments = [];
        for (const file of this.files) {
          const { key } = await S3UploadService.singleUpload(file, this.uploadFolder);
          attachments.push({
            s3Key: key,
            fileName: file.name,
            mimeType: file.type,
            fileType: this.fileType(file.type),
          });
        }
        const data = await this.service.submitFeedback(this.reportUid, {
          agree: this.agree,
          message: this.agree === false ? this.message : null,
          attachments,
        });
        // Backend returns handled errors as HTTP 200 with success:false.
        if (data && data.success === false) {
          const already = data.code === 409 || /already/i.test(data.message || "");
          this.$notify({
            text: already
              ? this.t("feedbackAlreadySubmitted")
              : data.message || this.t("uploadFailed"),
            type: "error",
          });
          return;
        }
        this.$emit("submitted");
      } catch (err) {
        const alreadySubmitted =
          err && err.data && (err.data.code === 409 || /already/i.test(err.data.message || ""));
        this.$notify({
          text: alreadySubmitted
            ? this.t("feedbackAlreadySubmitted")
            : this.t("uploadFailed"),
          type: "error",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.fb-card {
  border-radius: 8px;
}
.fb-choice {
  width: 52px !important;
  height: 52px !important;
}
.fb-dropzone {
  border: 2px dashed #1e1e1e;
  border-radius: 6px;
  padding: 36px 16px;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s ease;
}
.fb-dropzone--over {
  background: #f3f7ff;
  border-color: #2f6fed;
}
.fb-link {
  color: #2f6fed;
  font-weight: bold;
  text-decoration: underline;
}
</style>
